import { useState } from 'react';
import StoryPlayer from './components/StoryPlayer/StoryPlayer';
import Menu, { MenuType } from './components/UI/Menu';
import SettingsPanel from './components/UI/SettingsPanel';
import ChaptersPanel from './components/UI/ChaptersPanel';
import BuyMeCoffeeButton from './components/UI/BuyMeCoffeeButton';
import ScreenEffects from './components/Effects/ScreenEffects';
import { useStoryStore } from './store/storyStore';
import { useBlazorInterop } from './hooks/useBlazorInterop';

/**
 * Root App Component
 * Main application orchestrator for the story player
 */
function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isChaptersOpen, setIsChaptersOpen] = useState(false);
  const [menuType, setMenuType] = useState<MenuType>('main');

  const { isStoryLoaded, reset, showDonation, donationUsername, isStoryCompleted } = useStoryStore();

  console.log('[App] Rendering, isStoryLoaded:', isStoryLoaded);

  // Initialize Blazor interop at the app level
  useBlazorInterop();

  // Open main menu
  const handleOpenMainMenu = () => {
    setMenuType('main');
    setIsMenuOpen(true);
  };

  // Open pause menu
  const handleOpenPauseMenu = () => {
    setMenuType('pause');
    setIsMenuOpen(true);
  };

  // Close menu
  const handleCloseMenu = () => {
    setIsMenuOpen(false);
  };

  // Open settings
  const handleOpenSettings = () => {
    setIsSettingsOpen(true);
    setIsMenuOpen(false); // Close menu when opening settings
  };

  // Close settings
  const handleCloseSettings = () => {
    setIsSettingsOpen(false);
  };

  // Open chapters
  const handleOpenChapters = () => {
    setIsChaptersOpen(true);
    setIsMenuOpen(false); // Close menu when opening chapters
  };

  // Close chapters
  const handleCloseChapters = () => {
    setIsChaptersOpen(false);
  };

  // Start new game
  const handleNewGame = () => {
    reset();
    setIsMenuOpen(false);
    // Notify Blazor to start a new story
    if (window.NordicNeveInterop && window.NordicNeveInterop.notifyNavigateToScene) {
      // Navigate to first scene - Blazor will handle this
      window.NordicNeveInterop.notifyNavigateToScene('first-scene');
      console.log('[App] Starting new game');
    }
  };

  // Continue game
  const handleContinue = () => {
    setIsMenuOpen(false);
  };

  // Return to main menu
  const handleReturnToMainMenu = () => {
    reset();
    // Navigate back to Blazor home page (story selection)
    window.location.href = '/';
  };

  // Quit application
  const handleQuit = () => {
    if (window.confirm('Are you sure you want to quit?')) {
      window.close();
    }
  };

  return (
    <div className="w-full h-screen overflow-hidden bg-black">
      {/* Screen Effects (vignette, film grain, etc.) */}
      <ScreenEffects />

      {/* Story Player */}
      {isStoryLoaded ? (
        <StoryPlayer
          onOpenSettings={handleOpenSettings}
          onOpenMenu={handleOpenPauseMenu}
        />
      ) : (
        // Welcome screen when no story is loaded
        <div className="w-full h-full bg-black flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-6xl font-bold text-white mb-4 tracking-wide">
              NordicNeve
            </h1>
            <p className="text-2xl text-gray-400 mb-8 font-light">
              Interactive Storytelling Platform
            </p>
            <div className="bg-white/5 backdrop-blur-sm rounded border border-white/10 p-8 max-w-2xl">
              <p className="text-white text-lg mb-4">
                React Story Player is ready!
              </p>
              <p className="text-gray-400 mb-6">
                Waiting for story data from Blazor...
              </p>
              <button
                onClick={handleOpenMainMenu}
                className="px-6 py-3 bg-white text-black hover:bg-gray-200 rounded transition-colors font-medium"
              >
                Open Menu
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Menu (Main or Pause) */}
      <Menu
        isOpen={isMenuOpen}
        type={menuType}
        onClose={handleCloseMenu}
        onNewGame={handleNewGame}
        onContinue={isStoryLoaded ? handleContinue : undefined}
        onSettings={handleOpenSettings}
        onChapters={isStoryLoaded ? handleOpenChapters : undefined}
        onQuit={menuType === 'pause' ? handleReturnToMainMenu : handleQuit}
      />

      {/* Settings Panel */}
      <SettingsPanel isOpen={isSettingsOpen} onClose={handleCloseSettings} />

      {/* Chapters Panel */}
      <ChaptersPanel isOpen={isChaptersOpen} onClose={handleCloseChapters} />

      {/* Buy Me a Coffee Button - Only show when story is completed */}
      {isStoryLoaded && donationUsername && isStoryCompleted && (
        <BuyMeCoffeeButton
          username={donationUsername}
          show={showDonation && !isMenuOpen && !isSettingsOpen && !isChaptersOpen}
        />
      )}
    </div>
  );
}

export default App;
